import { useParams } from 'react-router-dom';
import { InlineNotification } from '@carbon/react';
import { Eval3NewContent } from './eval3-new-content';
import { Eval3PageProvider } from './context';

/**
 * Route entry for the Eval 3 master-data page. Reads `:packageId` (required) plus,
 * from the nested route, an optional `:workspaceId` (the management/browse view) and,
 * under it, an optional `:evalId` (`workspaces/:workspaceId/eval/:evalId`). Parses them
 * to numbers and renders the page content under `Eval3PageProvider`.
 *
 * Only a malformed PACKAGE id is a hard error — a garbage workspace / eval segment is
 * treated as absent (parses to null → renders the lower-identity view). Not-found ids
 * (well-formed but no such eval/workspace) never reach the FE: eval-URL resolution is DUAL —
 * SERVER-SIDE (`Packages::Eval3Controller` 404s a wrong id and redirects a missing one) on
 * cold loads, and CLIENT-SIDE (`Eval3LandingResolver`) for in-app nav — so the URL reaching
 * here is already valid + canonical.
 *
 * @return {JSX.Element} The page content, or an error notification for a malformed URL.
 */
export function Eval3NewPage({
  savedEvalsView = false,
}: {
  /** True only on the `saved-evals` route (the router passes it) — the Saved Evaluations browse view. */
  savedEvalsView?: boolean;
}) {
  const { packageId, workspaceId, evalId } = useParams<{
    packageId: string;
    workspaceId?: string;
    evalId?: string;
  }>();
  const parsedPackageId = Number(packageId);
  const isValidId = (id: number) => Number.isInteger(id) && id > 0;

  if (!isValidId(parsedPackageId)) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Invalid evaluation URL"
        subtitle="The package identifier is missing or not a number."
      />
    );
  }

  // The route's `:evalId` IS the `EvaluationCalculation` id → published as the page's
  // `calculationId` (null in workspace / package / draft mode) and sent on the data
  // requests as `calculationId`, which puts the BE in calc mode (eval-scoped filters
  // accepted, sourced from the calc's frozen blob). Never `evaluationId` (the BE 422s
  // it). A malformed segment parses to null → renders the lower-identity view.
  const parseSegment = (raw: string | undefined) => {
    if (raw === undefined || !isValidId(Number(raw))) {
      return null;
    }
    return Number(raw);
  };
  // The `eval/new` sentinel = a from-scratch draft under this workspace (no BE row yet →
  // `calculationId` null). Calculate persists it and routes to the real `eval/:id`.
  const isDraft = evalId === 'new';
  const calculationId = isDraft ? null : parseSegment(evalId);
  const parsedWorkspaceId = parseSegment(workspaceId);

  return (
    <Eval3PageProvider
      packageId={parsedPackageId}
      calculationId={calculationId}
      workspaceId={parsedWorkspaceId}
      isDraft={isDraft}
      isSavedEvalsView={savedEvalsView}
    >
      <Eval3NewContent />
    </Eval3PageProvider>
  );
}
